import React, { useState, useEffect } from "react";
import {
    ArrowLeft,
    FileSpreadsheet,
    Calendar,
    Layers,
    Eye,
    Activity,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    Loader2
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import { useNavigate } from "react-router-dom";
import { 
    useLazyBatchStatusQuery, 
    useLazyBatchSummaryQuery,
    useLazyBatchErrorsQuery,
    useLazyBatchHistoryQuery 
} from "../../../redux/actions/Admin/dashboardAdmin";
import BatchStatusModal from "../../../components/Admin/BatchStatusModal";
import BatchSummaryModal from "../../../components/Admin/BatchSummaryModal";
import BatchErrorsModal from "./BatchErrorsModal";
import { useAlert } from "../../../components/customer/AlertContext";

const InventoryUploadsScreen: React.FC = () => {
    const navigate = useNavigate();
    const { showConfirm } = useAlert();
    
    // Pagination Parameters
    const [pageNumber, setPageNumber] = useState<number>(1);
    const pageSize = 5;

    // RTK Query Hooks API Lazy Triggers
    const [triggerGetHistory, { data: historyResponse, isLoading: isLoadingHistory, isError: isHistoryError, isFetching: isFetchingHistory }] = useLazyBatchHistoryQuery();
    const [getBatchSummary] = useLazyBatchSummaryQuery();
    const [getBatchStatus] = useLazyBatchStatusQuery();
    const [getBatchErrors] = useLazyBatchErrorsQuery();
    
    // UI Modal Configurations
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [isErrorsModalOpen, setIsErrorsModalOpen] = useState(false);
    const [modalData, setModalData] = useState<any>(null);

    // Combine loading or background fetching state for total sync visibility
    const isCurrentlySyncing = isLoadingHistory || isFetchingHistory;

    // Fetch batch uploads dataset on page tracking increments
    const fetchHistoryLogs = () => {
        triggerGetHistory({ batchId: "", PageNumber: pageNumber, PageSize: pageSize });
    };

    useEffect(() => {
        fetchHistoryLogs();
    }, [pageNumber, triggerGetHistory]);

    const handleSummary = async (batchId: string) => {
        try {
            const response = await getBatchSummary(batchId).unwrap();
            setModalData(response);
            setIsSummaryModalOpen(true);
        } catch (error) {
            console.error("Summary Fetch Error", error);
        }
    };

    const handleStatus = async (batchId: string) => {
        try {
            const response = await getBatchStatus(batchId).unwrap();
            setModalData(response);
            setIsStatusModalOpen(true);
        } catch (error) {
            console.error("Status Fetch Error", error);
        }
    };

    const handleErrors = async (batchId: string) => {
        try {
            // Fetch clean structured JSON payload from your server errors endpoint
            const response = await getBatchErrors(batchId).unwrap();
            setModalData(response);
            setIsErrorsModalOpen(true);
        } catch (error) {
            console.error("Errors Diagnostics Fetch Error", error);
            showConfirm({
                title: "Diagnostics Error",
                message: "Failed to load validation error logs from the server. Please try again.",
                type: "error",
                confirmText: "OK",
                showCancel: false,
            });
        }
    };

    // Calculate metadata limits parameters
    const totalRecords = historyResponse?.totalRecords || 0;
    const recordsList = historyResponse?.data || [];
    const totalPages = Math.ceil(totalRecords / pageSize) || 1;

    return (
        <div className="min-h-screen bg-white px-4 py-6">
            <div className="max-w-7xl mx-auto">

                {/* HEADER SECTION WITH REFRESH BUTTON */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6 border-b border-gray-100 mb-8 gap-4">
                    <div className="mb-2 sm:mb-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-sm font-semibold text-iconColor hover:text-mainBrandColor transition-colors mb-2"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back to Upload Gateway
                        </button>
                        
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl sm:text-3xl font-black text-logoHeading">
                                System Upload History Logs
                            </h1>
                            {/* Inline structural syncing spinner badge */}
                            {isCurrentlySyncing && !isLoadingHistory && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-50 border border-slate-200 text-slate-500 animate-pulse">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-mainBrandColor" />
                                    <span>Syncing...</span>
                                </div>
                            )}
                        </div>

                        <p className="text-sm text-iconColor mt-1">
                            Monitor processing states, check layout diagnostics, and verify auto-published master count metrics.
                        </p>
                    </div>

                    {/* LIVE INSTANT REFRESH ACTION */}
                    <div className="flex items-center self-start sm:self-center">
                        <CommonButton
                            text={isCurrentlySyncing ? "Refreshing..." : "Refresh Data"}
                            variant="outline"
                            icon={<RefreshCw className={`w-4 h-4 ${isCurrentlySyncing ? "animate-spin text-mainBrandColor" : "text-slate-600"}`} />}
                            disabled={isCurrentlySyncing}
                            className="border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 disabled:opacity-75"
                            onClick={fetchHistoryLogs}
                        />
                    </div>
                </div>

                {/* LOADING ENGINE MASK INTERCEPT */}
                {isLoadingHistory && (
                    <div className="py-20 flex flex-col items-center justify-center gap-2 text-slate-400 text-sm">
                        <Loader2 className="w-8 h-8 animate-spin text-mainBrandColor" />
                        <span>Syncing systemic log repositories indexes...</span>
                    </div>
                )}

                {/* EXCEPTION INTERCEPT BLOCK ERROR ALERT CONTAINER */}
                {isHistoryError && (
                    <div className="text-center py-12 text-sm font-medium text-red-500 border border-red-100 bg-red-50 rounded-xl">
                        Failed to read system processing archive registries parameters from network context stream.
                    </div>
                )}

                {/* LIVE UPLOADS LIST VIEW */}
                {!isLoadingHistory && !isHistoryError && (
                    <div className="space-y-6">
                        {recordsList.length === 0 ? (
                            <div className="bg-gray-50 border border-dashed rounded-xl p-12 text-center">
                                <FileSpreadsheet className="mx-auto mb-3 w-12 h-12 text-gray-300" />
                                <p className="text-gray-500 font-medium">No inventory processing history items found inside archives.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {recordsList.map((log) => {
                                    // Status Logic Checks: If success count contains entries and has no errors, it is safely published automatically
                                    const isPublished = log.uploadStatus === "PUBLISHED" || log.processingStatus === "PUBLISHED" || (log.successCount > 0 && !log.hasErrors);

                                    return (
                                        <div
                                            key={log.batchId}
                                            className="bg-[#f9f9f9] rounded-2xl border border-accent/30 p-5 shadow-sm hover:shadow-md transition-all duration-200"
                                        >
                                            <div className="grid grid-cols-1 xl:grid-cols-3 items-center gap-6">

                                                {/* COLUMN 1: TRACK REFERENCE DETAILS IDENTITIES */}
                                                <div className="flex items-start gap-4 xl:col-span-1 min-w-0">
                                                    <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center shrink-0">
                                                        <FileSpreadsheet className="text-mainBrandColor w-6 h-6" />
                                                    </div>
                                                    <div className="space-y-1 min-w-0 flex-1">
                                                        <h3 className="text-base font-bold text-logoHeading whitespace-normal break-all leading-snug">
                                                            {log.fileName}
                                                        </h3>
                                                        <div className="flex flex-col gap-1 text-xs font-medium text-iconColor">
                                                            <span className="flex items-center gap-1">
                                                                <Layers className="w-3.5 h-3.5" /> {log.batchId}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="w-3.5 h-3.5" /> {new Date(log.uploadedOn).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* COLUMN 2: TOTAL ROW STATISTICS COUNTERS & COLORED STATUS BADGES */}
                                                <div className="flex flex-wrap items-center gap-3 xl:justify-center">
                                                    <div className="bg-green-50 text-green-700 text-xs font-bold px-2.5 py-1 rounded-lg border border-green-200">
                                                        Passed: {log.successCount}
                                                    </div>
                                                    <div className={`text-xs font-bold px-2.5 py-1 rounded-lg border ${log.failedCount > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                                                        Errors: {log.failedCount}
                                                    </div>

                                                    {/* LIVE CONTEXTUAL CHIP DEPLOYMENTS STATUS STRIP */}
                                                    {isPublished ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
                                                            Published Successfully
                                                        </span>
                                                    ) : (log.hasErrors || log.failedCount > 0) ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                                                            Fix Required
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                                            Processing...
                                                        </span>
                                                    )}
                                                </div>

                                                {/* COLUMN 3: ACTION CONTROLLER ACTIONS (ALL ALWAYS ENABLED) */}
                                                <div className="w-full xl:col-span-1">
                                                    <div className="flex flex-wrap items-center justify-start xl:justify-end gap-2">
                                                        <CommonButton
                                                            text="Status"
                                                            variant="outline"
                                                            icon={<Activity className="w-4 h-4" />}
                                                            className="bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-xs py-1.5 font-bold"
                                                            onClick={() => handleStatus(log.batchId)}
                                                        />

                                                        <CommonButton
                                                            text="Summary"
                                                            variant="outline"
                                                            icon={<Eye className="w-4 h-4" />}
                                                            className="bg-green-50 hover:bg-green-100 border border-green-200 text-green-800 text-xs py-1.5 font-bold"
                                                            onClick={() => handleSummary(log.batchId)}
                                                        />

                                                        <CommonButton
                                                            text="Diagnostics"
                                                            variant="outline"
                                                            icon={<AlertTriangle className="w-4 h-4" />}
                                                            className="bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-700 text-xs py-1.5 font-bold"
                                                            onClick={() => handleErrors(log.batchId)}
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* PAGINATION RUNNER BAR LAYER CONTROL TRACKER FOOTER */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-6">
                                <span className="text-xs text-iconColor font-semibold">
                                    Displaying logs records chunk page {pageNumber} of {totalPages} ({totalRecords} Total Items)
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        disabled={pageNumber === 1 || isCurrentlySyncing}
                                        onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                                        className="p-2 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        disabled={pageNumber === totalPages || isCurrentlySyncing}
                                        onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))}
                                        className="p-2 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* MODAL WORKSPACE LAYERS EXPANSIONS ATTACHMENTS */}
                {isStatusModalOpen && modalData && (
                    <BatchStatusModal
                        isOpen={isStatusModalOpen}
                        modalData={modalData}
                        onClose={() => {
                            setIsStatusModalOpen(false);
                            setModalData(null);
                        }}
                    />
                )}

                {isSummaryModalOpen && modalData && (
                    <BatchSummaryModal
                        isOpen={isSummaryModalOpen}
                        modalData={modalData}
                        onClose={() => {
                            setIsSummaryModalOpen(false);
                            setModalData(null);
                        }}
                    />
                )}

                {isErrorsModalOpen && modalData && (
                    <BatchErrorsModal
                        isOpen={isErrorsModalOpen}
                        modalData={modalData}
                        onClose={() => {
                            setIsErrorsModalOpen(false);
                            setModalData(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default InventoryUploadsScreen;